import React from 'react';
import { Icon } from '@growthaxl/web-foundation';

export interface SuccessActionToastProps {
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  /** Injected by react-toastify when rendered as toast content. */
  closeToast?: () => void;
}

/**
 * Success toast matching the host notification design:
 * green check icon, title, description, action link, dismiss control,
 * and a green bottom accent bar.
 */
export function SuccessActionToast({
  title,
  description,
  actionLabel = 'View in notifications',
  onAction,
  closeToast,
}: SuccessActionToastProps): React.ReactElement {
  return (
    <div
      data-testid="success-action-toast"
      className="gaxl-success-action-toast__card relative flex w-full gap-3 overflow-hidden rounded-12 bg-surface p-4 pr-10 shadow-md"
      role="status"
    >
      <div
        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-success-soft text-success-text"
        data-testid="success-action-toast-check"
        aria-hidden="true"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 8.5L6.5 12 13 4.5" />
        </svg>
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-text-primary">{title}</p>
        <p className="mt-0.5 text-sm text-text-secondary">{description}</p>
        {onAction && (
          <button
            type="button"
            data-testid="success-action-toast-link"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onAction();
            }}
            className="mt-2 inline cursor-pointer border-none bg-transparent p-0 text-left text-sm font-medium text-text-link underline decoration-current underline-offset-2 hover:opacity-90"
          >
            {actionLabel}
            <span aria-hidden="true" className="cursor-pointer">
              {' '}
              →
            </span>
          </button>
        )}
      </div>

      <button
        type="button"
        data-testid="success-action-toast-close"
        aria-label="Dismiss"
        onClick={closeToast}
        className="absolute right-2 top-2 flex size-7 items-center justify-center rounded-6 border-none bg-transparent text-text-tertiary hover:bg-surface-hover hover:text-text-primary"
      >
        <Icon name="close" className="!size-4 shrink-0" />
      </button>

      <span
        className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-success"
        aria-hidden="true"
      />
    </div>
  );
}
